import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  Divider,
  CircularProgress,
} from '@mui/material';
import { Close, Gavel, Schedule } from '@mui/icons-material';
import { AuctionService, AuctionBid } from '../../services/auctionService';

interface BidPlacementHistoryPopupProps {
  open: boolean;
  onClose: () => void;
  auctionId: string;
  productName: string;
}

const BidPlacementHistoryPopup: React.FC<BidPlacementHistoryPopupProps> = ({
  open,
  onClose,
  auctionId,
  productName,
}) => {
  const [loading, setLoading] = useState(false);
  const [bids, setBids] = useState<AuctionBid[]>([]);

  useEffect(() => {
    const fetchBids = async () => {
      if (!open || !auctionId) return;
      setLoading(true);
      try {
        const all = await AuctionService.getAuctionBids(auctionId);
        const sorted = [...all].sort((a, b) => new Date(b.bid_time).getTime() - new Date(a.bid_time).getTime());
        setBids(sorted);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to load bid history', e);
      } finally {
        setLoading(false);
      }
    };
    fetchBids();
  }, [open, auctionId]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: 3 },
          boxShadow: { xs: 'none', sm: '0 20px 40px rgba(0,0,0,0.15)' },
        },
      }}
    >
      <DialogTitle sx={{ pr: 6 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip icon={<Gavel />} label="Bid History" color="warning" sx={{ fontWeight: 'bold' }} />
          <Typography variant="h6" fontWeight={700}>
            {productName}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 12, top: 12 }}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {bids.length === 0 && (
              <Box sx={{ p: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  No bids have been placed yet.
                </Typography>
              </Box>
            )}
            {bids.map((bid, idx) => {
              const when = new Date(bid.bid_time);
              const primary = `$${bid.bid_amount.toFixed(2)}`;
              const secondary = `${when.toLocaleDateString()} • ${when.toLocaleTimeString()}${bid.status ? ` • ${bid.status}` : ''}`;
              return (
                <React.Fragment key={bid.id}>
                  <ListItem sx={{ py: 1.5, px: 2 }}>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: idx === 0 ? 'success.main' : 'grey.200', color: idx === 0 ? 'common.white' : 'text.primary' }}>
                        <Schedule />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={<Typography variant="h6" fontWeight={700}>{primary}</Typography>}
                      secondary={<Typography variant="body2" color="text.secondary">{secondary}</Typography>}
                    />
                    {idx === 0 && (
                      <Chip size="small" color="success" label="Highest" />
                    )}
                  </ListItem>
                  {idx < bids.length - 1 && <Divider component="li" />}
                </React.Fragment>
              );
            })}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BidPlacementHistoryPopup;


